import {
  IonAccordion,
  IonAccordionGroup,
  IonIcon,
  IonInput,
  IonLabel,
  IonList,
  IonListHeader,
  IonRouterLink,
  IonText,
  IonToolbar,
  useIonAlert,
  useIonViewDidEnter,
} from '@ionic/react';
import { Form, Formik, FormikProps } from 'formik';
import { arrowForwardOutline } from 'ionicons/icons';
import { cloneDeep, isEmpty, isEqual, merge } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { usePermissions } from '../../../../../../api/authorization.provider';
import {
  Scratchpad,
  useGetUserQuery,
} from '../../../../../../api/graphql/generated';
import { useHasFeature } from '../../../../../../api/hooks/feature.hooks';
import {
  useGetMachinePreference,
  useSetMachinePreferenceLazy,
} from '../../../../../../api/hooks/preference.hooks';
import { useGetUnitOfMeasureSystem } from '../../../../../../api/hooks/user-preference.hooks';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { LoadingButton } from '../../../../../../components/button/loading-button';
import { WithConfirmation } from '../../../../../../components/button/with-confirmation';
import { IonFormTextareaField } from '../../../../../../components/form/ion-form-textarea.field';
import { IonFormField } from '../../../../../../components/form/ion-form.field';
import { IonItem } from '../../../../../../components/ionic/IonItem';
import { useScratchpadServiceContext } from '../../../../../../components/machine/scratchpad/scratchpad-service-context-provider';
import { ContentWithFooter } from '../../../../../../components/ui/content-with-footer';
import { FieldLocked } from '../../../../../../components/ui/field-locked';
import {
  trackCobotWelderPartSettingsSaveAsDefault,
  trackCobotWelderResetPartSettings,
  trackCreateScratchpad,
  trackUpdatedScratchpad,
} from '../../../../../../config/analytics';
import i18n from '../../../../../../i18n';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../../../pages/machine/machine-context-provider';
import { isEnter } from '../../../../../../util/keyboard';
import { logger } from '../../../../../../util/logger';
import { QNear, Waypoint } from '../../../../../../util/machine-variable.utils';
import { getUsername } from '../../../../../../util/user-utils';
import { CaptureWaypointButton } from '../../../../shared/component/button/capture-waypoint-button';
import { PickerInput } from '../../../../shared/component/widget/picker-input';
import { SliderInput } from '../../../../shared/component/widget/slider-input';
import { SwitchInput } from '../../../../shared/component/widget/switch-input';
import { WidgetLabel } from '../../../../shared/component/widget/widget-label';
import { PickerOption } from '../../../../shared/component/widget/widget.types';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import { useToolsNotFoundAlert } from '../../../../shared/hook/part-detail.hooks';
import { Flow, ToolConfig } from '../../../../shared/model/flow.types';
import { isToolEqual } from '../../../../shared/util/flow-config.util';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { WeldEquipmentSettings } from '../settings/weld-equipment-settings';
import {
  buildConfigCustomFieldsValidationSchema,
  convertEquipmentConfigUom,
} from '../settings/weld-equipment-settings.hooks';

const scratchpadDefaultAppDataConfigSchema = yup.object().shape({
  arcOn: yup.boolean().required(),
  betweenSpeed: yup.number().required(),
  gasFlowRate: yup.number().required(),
  arcDetectionFailureTime: yup.number(),
  home: yup.object().required(),
  ioMapping: yup.object().required(),
  torch: yup.object().required(),
  welder: yup.object().required(),
  wireDiameter: yup.number().required(),
  materialType: yup.string().required(),
  weave: yup.object().shape({
    disableCornerRatioCheck: yup.boolean().required(),
  }),
});

const scratchpadDefaultAppDataSchema = yup.object().shape({
  config: scratchpadDefaultAppDataConfigSchema,
});

const scratchpadSchema = yup.object().shape({
  description: yup.string().nullable(),
  name: yup.string().required('*'),
  appId: yup.string().required('*'),
  pastedAppDataRef: yup.string(),
  appData: scratchpadDefaultAppDataSchema,
});

const APP_DATA_CONFIG_PREFIX = `appData.config`;

export interface WeldPartDetailProps {
  afterSave?: (scratchpad: Scratchpad) => void;
  readOnly?: boolean;
}

const WeldPartDetail = (props: WeldPartDetailProps) => {
  const { afterSave, readOnly } = props;
  const { machine } = useMachineContext();
  const { flowService } = useFlowServices<BeaconWeldingData.FlowConfig>();
  const { scratchpad, save, remove, restore } = useScratchpadServiceContext();

  const system = useGetUnitOfMeasureSystem();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isTogglingActive, setTogglingActive] = useState<boolean>(false);

  const canPasteAppdata = useHasFeature<boolean | undefined>(
    'beacon-scratchpad-paste-appdata'
  );

  const canDisableWeaveCornerRatioCheck = useHasFeature<boolean | undefined>(
    'beacon-welding-weave-disable-corner-ratio'
  );

  const permissions = usePermissions('scratchpad', scratchpad);
  const canDelete = permissions.delete ?? false;

  const canPerformSave =
    scratchpad.id === undefined ? permissions.create : permissions.update;

  const formikRef = useRef<FormikProps<Partial<Scratchpad>> | null>(null);

  const isEditingExisting = scratchpad?.name ? true : false;
  const nameFieldRef = useRef<HTMLIonInputElement>(null);
  const focusName = async () => {
    nameFieldRef.current?.setFocus();
  };

  const createdById = scratchpad.createdBy ?? i18n.t('common:system');
  const updatedById = scratchpad.updatedBy ?? scratchpad.createdBy;

  const { data: getCreatedByUserData } = useGetUserQuery({
    variables: {
      input: {
        id: createdById,
      },
    },
    skip: scratchpad.createdBy === undefined,
    fetchPolicy: 'cache-first',
  });

  const { data: getUpdatedByUserData } = useGetUserQuery({
    variables: {
      input: {
        id: updatedById,
      },
    },
    skip: updatedById === undefined,
    fetchPolicy: 'cache-first',
  });

  const createdByName = getUsername(getCreatedByUserData?.getUser);
  const updatedByName = getUsername(getUpdatedByUserData?.getUser);

  const [showSavePartDefaultsAlert] = useIonAlert();

  const [setPartConfigDefaultPreference] =
    useSetMachinePreferenceLazy<Partial<BeaconWeldingData.FlowConfig>>();

  const resetPartSettingsToDefault = () => {
    const setFieldValue = formikRef.current?.setFieldValue;

    if (!setFieldValue) {
      return;
    }

    trackCobotWelderResetPartSettings();

    const defaultConfig = flowService.getDefaultFlowConfig();

    setFieldValue(`${APP_DATA_CONFIG_PREFIX}.torch`, defaultConfig.torch);
    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.wireDiameter`,
      defaultConfig.wireDiameter
    );

    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.materialType`,
      defaultConfig.materialType
    );

    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.gasFlowRate`,
      defaultConfig.gasFlowRate
    );

    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.arcDetectionFailureTime`,
      defaultConfig.arcDetectionFailureTime
    );

    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.home.${BeaconWeldingData.FlowItemParameterRef.WAYPOINT}`,
      defaultConfig.home.waypoint
    );

    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.home.${BeaconWeldingData.FlowItemParameterRef.QNEAR}`,
      defaultConfig.home.qnear
    );

    setFieldValue(
      `${APP_DATA_CONFIG_PREFIX}.weave.disableCornerRatioCheck`,
      defaultConfig?.weave?.disableCornerRatioCheck ?? false
    );
  };

  const toggleActivation = async () => {
    setTogglingActive(true);

    if (scratchpad.active) {
      logger.info('removing scratchpad', { id: scratchpad.id });
      await remove(scratchpad.id);
    } else {
      logger.info('restoring scratchpad', { id: scratchpad.id });
      await restore(scratchpad.id);
    }

    setTogglingActive(false);
  };

  useIonViewDidEnter(() => {
    if (nameFieldRef.current) {
      focusName();
    }
  }, []);

  const torchOptions: PickerOption<ToolConfig>[] = useMemo(() => {
    const torches = flowService.getToolsForAssignedEquipment();

    if (torches.length === 0) {
      logger.error('no torches found', {
        machine,
      });
    }

    return torches.map((options) => {
      return {
        label: options.label,
        value: options.config,
      };
    });
  }, [flowService, machine]);

  const defaultPartConfigPreference =
    useGetMachinePreference<BeaconWeldingData.FlowConfig>(
      BeaconWeldingData.BeaconWeldingMachinePreference.PART_CONFIG_DEFAULTS
    );

  const initialValues: Partial<Scratchpad> = useMemo(() => {
    const preferenceConfig: Partial<BeaconWeldingData.FlowConfig> =
      defaultPartConfigPreference?.value
        ? mapMachinePreferenceFlowConfig(defaultPartConfigPreference.value)
        : {};

    const preferredTorch = preferenceConfig.torch;
    if (preferredTorch) {
      const defaultTorch = torchOptions.find((option) => {
        return isToolEqual(option.value, preferredTorch);
      });

      if (!defaultTorch) {
        logger.error('preferred torch is not included in available torches', {
          torch: preferredTorch,
          torchOptions,
        });
        preferenceConfig.torch = undefined;
      } else {
        // use the exact object from the options to ensure updates are applied
        preferenceConfig.torch = defaultTorch.value;
      }
    }

    const appData = merge(
      {
        config: flowService.getDefaultFlowConfig(),
      },
      {
        config: preferenceConfig,
      },
      scratchpad.appData
    );

    if (appData.config.welder.customFields) {
      appData.config.welder.customFields = convertEquipmentConfigUom({
        system,
        welder: appData.config.welder,
        standardize: false,
      });
    }

    return {
      ...scratchpad,
      appData,
    };
  }, [
    defaultPartConfigPreference?.value,
    flowService,
    scratchpad,
    system,
    torchOptions,
  ]);

  const onSubmit = useCallback(
    async (input: Partial<Scratchpad> & { pastedAppData?: string }) => {
      setIsSaving(true);

      const { pastedAppData, ...partsToSave } = cloneDeep(input);

      if (pastedAppData) {
        try {
          partsToSave.appData = JSON.parse(pastedAppData);
        } catch (error) {
          logger.error(
            'Could not submit part detail form. Failed to parse pasted app data',
            { error }
          );
          return;
        }
      }

      if (partsToSave.appData.config.welder.customFields) {
        partsToSave.appData.config.welder.customFields =
          convertEquipmentConfigUom({
            system,
            welder: partsToSave.appData.config.welder,
            standardize: true,
          });
      }

      const internalSave = async (options?: { savePartDefaults?: boolean }) => {
        const { savePartDefaults = false } = options ?? {};

        if (savePartDefaults) {
          const config = (partsToSave.appData as Flow)?.config;

          await setPartConfigDefaultPreference({
            key: BeaconWeldingData.BeaconWeldingMachinePreference
              .PART_CONFIG_DEFAULTS,
            value: mapMachinePreferenceFlowConfig(config),
          });
        }

        // we want to save the delta when editing an existing scratchpad
        const result = await save(partsToSave as Scratchpad, {
          saveAsDelta: isEditingExisting,
        });

        setIsSaving(false);

        if (result !== undefined) {
          if (input.id) {
            trackUpdatedScratchpad(BeaconWeldingData.APP_SLUG);
          } else {
            trackCreateScratchpad(BeaconWeldingData.APP_SLUG);
          }
        }

        if (result?.id) {
          if (typeof afterSave === 'function') {
            afterSave(result);
          }
        }
      };

      const originalConfig = (initialValues.appData as Flow).config;
      const updatedConfig = (partsToSave.appData as Flow).config;

      if (isEqual(originalConfig, updatedConfig)) {
        internalSave();
      } else {
        showSavePartDefaultsAlert(
          getLocalizedText('welding:part_config_change') ?? '',
          [
            {
              text: i18n.t('common:yes'),
              handler: () => {
                trackCobotWelderPartSettingsSaveAsDefault(true);
                internalSave({ savePartDefaults: true });
              },
            },
            {
              text: i18n.t('common:no'),
              role: 'cancel',
              handler: () => {
                trackCobotWelderPartSettingsSaveAsDefault(false);
                internalSave();
              },
            },
          ]
        );
      }
    },
    [
      afterSave,
      initialValues.appData,
      isEditingExisting,
      save,
      setPartConfigDefaultPreference,
      showSavePartDefaultsAlert,
      system,
    ]
  );

  useToolsNotFoundAlert({
    show:
      torchOptions.length === 0 || isEmpty(initialValues.appData.config.torch),
  });

  const validationSchema = useMemo(() => {
    if (
      initialValues.appData.config.welder.model ===
      BeaconWeldingData.Welder.MILLER_DELTAWELD
    ) {
      const deltaweldCustomFieldsSchema =
        buildConfigCustomFieldsValidationSchema({
          system,
          welder: initialValues.appData.config.welder,
        });

      let appDataConfigSchema: yup.ObjectSchema<any>;
      if (!deltaweldCustomFieldsSchema) {
        logger.warn(
          'Failed to build validation schema for deltaweld custom fields',
          { system, welder: initialValues.appData.config.welder ?? 'undefined' }
        );
        appDataConfigSchema = scratchpadDefaultAppDataConfigSchema;
      } else {
        appDataConfigSchema = scratchpadDefaultAppDataConfigSchema.concat(
          yup.object().shape({
            welder: deltaweldCustomFieldsSchema,
          })
        );
      }

      const appDataSchema = scratchpadDefaultAppDataSchema.concat(
        yup.object().shape({ config: appDataConfigSchema })
      );

      return scratchpadSchema.concat(
        yup.object().shape({ appData: appDataSchema })
      );
    } else {
      return scratchpadSchema;
    }
  }, [initialValues.appData.config.welder, system]);

  const activationButtonDisabled =
    !canDelete ||
    isSaving ||
    isTogglingActive ||
    machine?.active === false ||
    readOnly;

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize={true}
      validateOnMount={true}
      innerRef={(instance) => (formikRef.current = instance)}
    >
      {({ isValid, dirty, setFieldValue, submitForm, values }) => {
        const isSaveDisabled =
          !isValid || !dirty || isSaving || isTogglingActive || readOnly;

        const footer = (
          <IonToolbar>
            <RightAlignedButtons>
              {isEditingExisting && (
                <WithConfirmation
                  header={
                    scratchpad.active
                      ? i18n.t('common:scratchpad_deactivate_confirmation')
                      : i18n.t('common:scratchpad_activate_confirmation')
                  }
                >
                  {({ onConfirmed }) => {
                    return (
                      <LoadingButton
                        loading={isTogglingActive}
                        buttonProps={{
                          color: scratchpad.active ? 'danger' : 'primary',
                          disabled: activationButtonDisabled,
                          fill: 'outline',
                          onClick: () =>
                            onConfirmed({ onConfirmation: toggleActivation }),
                        }}
                      >
                        {scratchpad.active
                          ? i18n.t('common:scratchpad_deactivate')
                          : i18n.t('common:scratchpad_activate')}
                      </LoadingButton>
                    );
                  }}
                </WithConfirmation>
              )}
              {canPerformSave && (
                <LoadingButton
                  loading={isSaving}
                  buttonProps={{
                    onClick: () => {
                      submitForm();
                    },
                    disabled: isSaveDisabled,
                  }}
                >
                  {isEditingExisting ? (
                    i18n.t('common:save')
                  ) : (
                    <>
                      {i18n.t('common:next')}
                      <IonIcon icon={arrowForwardOutline} slot="end" />
                    </>
                  )}
                </LoadingButton>
              )}
            </RightAlignedButtons>
          </IonToolbar>
        );

        return (
          <ContentWithFooter footer={footer}>
            <Form data-cy="scratchpad-form">
              <IonList lines="none">
                <IonFormField
                  label={i18n.t('common:scratchpad_name')}
                  name="name"
                  inputProps={{
                    type: 'text',
                    autocorrect: 'on',
                    autocapitalize: 'on',
                    ref: nameFieldRef,
                    placeholder: i18n.t('common:scratchpad_name_placeholder'),
                    disabled: readOnly,
                    enterkeyhint: 'next',
                    required: true,
                    onKeyDown: (
                      e: React.KeyboardEvent<HTMLIonInputElement>
                    ) => {
                      if (isEnter(e)) {
                        submitForm();
                      }
                    },
                  }}
                />
                <IonFormTextareaField
                  label={i18n.t('common:scratchpad_description')}
                  className="form-textarea"
                  name="description"
                  inputProps={{
                    autocapitalize: 'on',
                    placeholder: i18n.t(
                      'common:scratchpad_description_placeholder'
                    ),
                    disabled: readOnly,
                    enterkeyhint: 'next',
                  }}
                />
                {createdByName && (
                  <IonItem lines={'full'}>
                    <IonInput
                      labelPlacement={'floating'}
                      type="text"
                      readonly={true}
                      value={createdByName}
                    >
                      <div slot="label">
                        <IonText color="primary">
                          {i18n.t('common:created_by')}{' '}
                          <FieldLocked show={true} />
                        </IonText>
                      </div>
                    </IonInput>
                  </IonItem>
                )}
                {updatedByName && (
                  <IonItem lines={'full'}>
                    <IonInput
                      labelPlacement={'floating'}
                      type="text"
                      readonly={true}
                      value={updatedByName}
                    >
                      <div slot="label">
                        <IonText color="primary">
                          {i18n.t('common:last_updated_by')}{' '}
                          <FieldLocked show={true} />
                        </IonText>
                      </div>
                    </IonInput>
                  </IonItem>
                )}
                {canPasteAppdata && !isEditingExisting && (
                  <IonFormTextareaField
                    label={i18n.t('common:scratchpad_app_data') ?? 'App Data'}
                    name="pastedAppData"
                    inputProps={{
                      autoGrow: false,
                      placeholder: `{"items":...}`,
                      disabled: readOnly,
                    }}
                    validate={(value): string | undefined => {
                      if (value === undefined || value === '') {
                        return;
                      }

                      try {
                        JSON.parse(value);
                      } catch (e) {
                        return (
                          i18n.t('common:scratchpad_invalid_json') ??
                          'Invalid JSON'
                        );
                      }
                    }}
                  />
                )}
              </IonList>
              <IonList lines="full">
                <IonListHeader>
                  <IonLabel>{getLocalizedText('welding:settings')}</IonLabel>
                </IonListHeader>
                <IonItem className="widget transparent-background">
                  <WidgetLabel
                    label={getLocalizedText('welding:home_waypoint')}
                    forceDisable={readOnly}
                  />
                  <CaptureWaypointButton
                    cancelButtonText={getLocalizedText(
                      'welding:home_waypoint_touch_up_cancel'
                    )}
                    captureButtonText={getLocalizedText(
                      'welding:home_waypoint_touch_up'
                    )}
                    onCapture={(waypoint: Waypoint, qnear: QNear) => {
                      setFieldValue(
                        `${APP_DATA_CONFIG_PREFIX}.home.${BeaconWeldingData.FlowItemParameterRef.WAYPOINT}`,
                        waypoint
                      );
                      setFieldValue(
                        `${APP_DATA_CONFIG_PREFIX}.home.${BeaconWeldingData.FlowItemParameterRef.QNEAR}`,
                        qnear
                      );
                    }}
                    disabled={readOnly}
                  />
                </IonItem>
                <PickerInput
                  name={`${APP_DATA_CONFIG_PREFIX}.torch`}
                  options={torchOptions}
                  placeholder={getLocalizedText('welding:make_selection')}
                  label={getLocalizedText('welding:torch')}
                  disabled={readOnly}
                />
              </IonList>
              <WeldEquipmentSettings
                configPrefix={APP_DATA_CONFIG_PREFIX}
                equipment={values.appData.config.welder.model}
              />
              <IonAccordionGroup style={{ paddingBottom: '10px' }}>
                <IonAccordion>
                  <IonItem
                    slot="header"
                    style={{ fontWeight: 'bold', fontSize: 'medium' }}
                  >
                    <IonLabel>
                      {getLocalizedText('welding:advanced_settings')}
                    </IonLabel>
                  </IonItem>
                  <div slot="content">
                    <IonList>
                      {canDisableWeaveCornerRatioCheck && (
                        <SwitchInput
                          name={`${APP_DATA_CONFIG_PREFIX}.weave.disableCornerRatioCheck`}
                          label={getLocalizedText(
                            'welding:weave_around_corners'
                          )}
                        />
                      )}
                      <SliderInput
                        name={`${APP_DATA_CONFIG_PREFIX}.gasFlowRate`}
                        label={getLocalizedText('welding:gas_flow_rate')}
                        min={0}
                        max={100}
                        step={1}
                        disabled={readOnly}
                      />
                      <SliderInput
                        name={`${APP_DATA_CONFIG_PREFIX}.arcDetectionFailureTime`}
                        label={getLocalizedText(
                          'welding:arc_detection_failure_time'
                        )}
                        min={0.5}
                        max={3}
                        step={0.1}
                        disabled={readOnly}
                      />
                    </IonList>
                    {canPerformSave && (
                      <div className="ion-padding ion-text-center">
                        <IonRouterLink
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();

                            resetPartSettingsToDefault();
                          }}
                        >
                          {getLocalizedText(
                            'welding:reset_part_settings_to_default'
                          )}
                        </IonRouterLink>
                      </div>
                    )}
                  </div>
                </IonAccordion>
              </IonAccordionGroup>
              {isEditingExisting && !canDelete && (
                <IonLabel
                  className="ion-padding content-center ion-text-center"
                  color={'medium'}
                >
                  {i18n.t('common:deactivate_not_allowed', {
                    item: i18n.t('common:part'),
                  })}
                </IonLabel>
              )}
            </Form>
          </ContentWithFooter>
        );
      }}
    </Formik>
  );
};

const mapMachinePreferenceFlowConfig = (
  config: Partial<BeaconWeldingData.FlowConfig>
): Partial<BeaconWeldingData.FlowConfig> => {
  return {
    home: config.home,
    torch: config.torch,
    gasFlowRate: config.gasFlowRate,
    arcDetectionFailureTime: config?.arcDetectionFailureTime,
    weave: config.weave,
  };
};

export { WeldPartDetail };
